import { DOCUMENT_STATUS, REPORT_TYPE } from '@/types/document';
import { JOB_TYPE, JOB_STATUS } from '@/types/job';
import { ACTIONS } from '@/types/taskActions';

/**
 * JSON type definition for Supabase
 */
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

/**
 * Database schema definition
 */
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          name: string | null
          username: string | null
          avatar_url: string | null
          role: string | null
          email: string | null
          updated_at: string | null
          created_at: string
          system_role: string | null
          business_role: string | null
          report_type: string | null
        }
        Insert: {
          id: string
          name?: string | null
          username?: string | null
          avatar_url?: string | null
          role?: string | null
          email?: string | null
          updated_at?: string | null
          created_at?: string
          system_role: string | null
          business_role?: string | null
          report_type?: string | null
        }
        Update: {
          id?: string
          name?: string | null
          username?: string | null
          avatar_url?: string | null
          role?: string | null
          email?: string | null
          updated_at?: string | null
          created_at?: string
          system_role: string | null
          business_role?: string | null
          report_type?: string | null
        }
      }
      files: {
        Row: {
          id: string
          type: string
          name: string
          url: string
        }
        Insert: {
          id?: string
          type: string
          name: string
          url: string
        }
        Update: {
          id?: string
          type?: string
          name?: string
          url?: string
        }
      }
      documents: {
        Row: {
          uuid: string
          name: string
          description: string | null
          created_at: string
          updated_at: string | null
          status: DOCUMENT_STATUS
          created_by: string
          updated_by: string | null
          file_id: string | null
          forecast_id: string | null
          email_id: string | null
          report_type: REPORT_TYPE | null
        }
        Insert: {
          uuid?: string
          name: string
          description?: string | null
          created_at?: string
          updated_at?: string | null
          status: DOCUMENT_STATUS
          created_by: string
          updated_by?: string | null
          file_id?: string | null
          forecast_id?: string | null
          email_id?: string | null
          report_type?: REPORT_TYPE | null
        }
        Update: {
          uuid?: string
          name?: string
          description?: string | null
          created_at?: string
          updated_at?: string | null
          status?: DOCUMENT_STATUS
          created_by?: string
          updated_by?: string | null
          file_id?: string | null
          forecast_id?: string | null
          email_id?: string | null
          report_type?: REPORT_TYPE | null
        }
      }
      documents_legacy: {
        Row: {
          id: string
          name: string
          description: string | null
          file_type: string | null
          file_size: number | null
          created_at: string
          status: 'CREATING' | 'REVIEW_CREATED' | 'EMAIL_PENDING' | 'READY_FOR_DOWNLOAD' | 'DOWNLOADED' | 'UPLOADED_CHECKING' | 'ADJUSTMENT_NEEDED' | 'REVISION_NEEDED'
          created_by: string
          template_id?: string | null
          forecast_id?: string | null
          content?: Json | null
          type: 'OPERATIONS_MANUAL' | 'MARKETING_PLAN' | 'SALES_REPORT' | 'CUSTOMER_FEEDBACK' | 'MAINTENANCE_CHECKLIST' | 'EMPLOYEE_HANDBOOK'
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          file_type?: string | null
          file_size?: number | null
          created_at?: string
          status?: 'CREATING' | 'REVIEW_CREATED' | 'EMAIL_PENDING' | 'READY_FOR_DOWNLOAD' | 'DOWNLOADED' | 'UPLOADED_CHECKING' | 'ADJUSTMENT_NEEDED' | 'REVISION_NEEDED'
          user_id: string
          template_id?: string | null
          forecast_id?: string | null
          content?: Json | null
          type: 'OPERATIONS_MANUAL' | 'MARKETING_PLAN' | 'SALES_REPORT' | 'CUSTOMER_FEEDBACK' | 'MAINTENANCE_CHECKLIST' | 'EMPLOYEE_HANDBOOK'
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          file_type?: string | null
          file_size?: number | null
          created_at?: string
          status?: 'CREATING' | 'REVIEW_CREATED' | 'EMAIL_PENDING' | 'READY_FOR_DOWNLOAD' | 'DOWNLOADED' | 'UPLOADED_CHECKING' | 'ADJUSTMENT_NEEDED' | 'REVISION_NEEDED'
          user_id?: string
          template_id?: string | null
          forecast_id?: string | null
          content?: Json | null
          type: 'OPERATIONS_MANUAL' | 'MARKETING_PLAN' | 'SALES_REPORT' | 'CUSTOMER_FEEDBACK' | 'MAINTENANCE_CHECKLIST' | 'EMPLOYEE_HANDBOOK'
        }
      }
      forecasting_process: {
        Row: {
          id: string
          name: string
          current_step: number
          start_date: string
          end_date: string | null
          status: 'CREATED' | 'CONSOLIDATED' | 'COMPLETED' | 'CANCELLED'
          created_at: string
        }
        Insert: {
          id?: string
          name: string
          current_step?: number
          start_date: string
          end_date?: string | null
          status?: 'CREATED' | 'CONSOLIDATED' | 'COMPLETED' | 'CANCELLED'
          created_at?: string
        }
        Update: {
          id?: string
          name?: string
          current_step?: number
          start_date?: string
          end_date?: string | null
          status?: 'CREATED' | 'CONSOLIDATED' | 'COMPLETED' | 'CANCELLED'
          created_at?: string
        }
      }
      comments: {
        Row: {
          id: string
          created_at: string
          created_by: string
          content: string
          reference_id: string
        }
        Insert: {
          id?: string
          created_at?: string
          created_by: string
          content: string
          reference_id: string
        }
        Update: {
          id?: string
          created_at?: string
          created_by?: string
          content?: string
          reference_id?: string
        }
      }
      email_templates: {
        Row: {
          id: string
          category_id: string
          template_name: string
          subject: string
          content: string
          created_at: string
          updated_at: string | null
          created_by: string
          is_system: boolean
        }
        Insert: {
          id?: string
          category_id: string
          template_name: string
          subject: string
          content: string
          created_at?: string
          updated_at?: string | null
          created_by: string
          is_system?: boolean
        }
        Update: {
          id?: string
          category_id?: string
          template_name?: string
          subject?: string
          content?: string
          created_at?: string
          updated_at?: string | null
          created_by?: string
          is_system?: boolean
        }
      }
      jobs: {
        Row: {
          id: string
          type: JOB_TYPE
          status: JOB_STATUS
          created_at: string
          updated_at: string | null
          forecast_id: string | null
        }
        Insert: {
          id?: string
          type: JOB_TYPE
          status: JOB_STATUS
          created_at?: string
          updated_at?: string | null
          forecast_id?: string | null
        }
        Update: {
          id?: string
          type?: JOB_TYPE
          status?: JOB_STATUS
          created_at?: string
          updated_at?: string | null
          forecast_id?: string | null
        }
      }
      due_dates: {
        Row: {
          id: string
          created_at: string
          updated_at: string | null
          action: ACTIONS
          document_type: REPORT_TYPE
          forecast_id: string
          absolute: string | null
          relative_hours: number | null
          consider_workdays: boolean
        }
        Insert: {
          id?: string
          created_at?: string
          updated_at?: string | null
          action: ACTIONS
          document_type: REPORT_TYPE
          forecast_id: string
          absolute?: string | null
          relative_hours?: number | null
          consider_workdays: boolean
        }
        Update: {
          id?: string
          created_at?: string
          updated_at?: string | null
          action?: ACTIONS
          document_type?: REPORT_TYPE
          forecast_id?: string
          absolute?: string | null
          relative_hours?: number | null
          consider_workdays?: boolean
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
  }
}

/**
 * Convenience types for working with specific tables
 */
export type Profile = Database['public']['Tables']['profiles']['Row']
export type File = Database['public']['Tables']['files']['Row']
export type Document = Database['public']['Tables']['documents']['Row']
export type DocumentLegacy = Database['public']['Tables']['documents_legacy']['Row']
export type ForecastingProcess = Database['public']['Tables']['forecasting_process']['Row']
export type Comment = Database['public']['Tables']['comments']['Row']
export type EmailTemplate = Database['public']['Tables']['email_templates']['Row']
export type Job = Database['public']['Tables']['jobs']['Row']
export type DueDates = Database['public']['Tables']['due_dates']['Row']

/**
 * Types for inserting data
 */
export type ProfileInsert = Database['public']['Tables']['profiles']['Insert']
export type FileInsert = Database['public']['Tables']['files']['Insert']
export type DocumentInsert = Database['public']['Tables']['documents']['Insert']
export type DocumentLegacyInsert = Database['public']['Tables']['documents_legacy']['Insert']
export type ForecastingProcessInsert = Database['public']['Tables']['forecasting_process']['Insert']
export type CommentInsert = Database['public']['Tables']['comments']['Insert']
export type EmailTemplateInsert = Database['public']['Tables']['email_templates']['Insert']
export type JobInsert = Database['public']['Tables']['jobs']['Insert']
export type DueDatesInsert = Database['public']['Tables']['due_dates']['Insert']

/**
 * Types for updating data
 */
export type ProfileUpdate = Database['public']['Tables']['profiles']['Update']
export type FileUpdate = Database['public']['Tables']['files']['Update']
export type DocumentUpdate = Database['public']['Tables']['documents']['Update']
export type DocumentLegacyUpdate = Database['public']['Tables']['documents_legacy']['Update']
export type ForecastingProcessUpdate = Database['public']['Tables']['forecasting_process']['Update']
export type CommentUpdate = Database['public']['Tables']['comments']['Update']
export type EmailTemplateUpdate = Database['public']['Tables']['email_templates']['Update']
export type JobUpdate = Database['public']['Tables']['jobs']['Update']
export type DueDatesUpdate = Database['public']['Tables']['due_dates']['Update']

/**
 * Template types enum
 */
export enum TemplateType {
  FORECAST = 'FORECAST',
  REPORT = 'REPORT',
  EMAIL = 'EMAIL',
  OTHER = 'OTHER'
}

/**
 * Re-export document enums for backward compatibility
 */
export { DOCUMENT_STATUS, REPORT_TYPE, JOB_TYPE, JOB_STATUS };

/**
 * Template status enum
 */
export enum TemplateStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

/**
 * Template type definition
 * This is used by the template-storage feature
 */
export interface Template {
  id: string;
  name: string;
  description: string | null;
  file_type: string | null;
  file_size: number | null;
  created_at: string;
  status: 'pending' | 'approved' | 'rejected';
  is_ai_generated: boolean;
  user_id: string;
  storage_path: string | null;
  original_filename: string | null;
  planner_id: string | null;
  validation_info: Json | null;
  last_accessed: string | null;
  access_count: number | null;
}

/**
 * Template insert type definition
 */
export interface TemplateInsert {
  id?: string;
  name: string;
  description?: string | null;
  file_type?: string | null;
  file_size?: number | null;
  created_at?: string;
  status?: 'pending' | 'approved' | 'rejected';
  is_ai_generated?: boolean;
  user_id: string;
  storage_path?: string | null;
  original_filename?: string | null;
  planner_id?: string | null;
  validation_info?: Json | null;
  last_accessed?: string | null;
  access_count?: number | null;
}

/**
 * Template update type definition
 */
export interface TemplateUpdate {
  id?: string;
  name?: string;
  description?: string | null;
  file_type?: string | null;
  file_size?: number | null;
  created_at?: string;
  status?: 'pending' | 'approved' | 'rejected';
  is_ai_generated?: boolean;
  user_id?: string;
  storage_path?: string | null;
  original_filename?: string | null;
  planner_id?: string | null;
  validation_info?: Json | null;
  last_accessed?: string | null;
  access_count?: number | null;
}

/**
 * Template validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors?: string[];
  warnings?: string[];
  details?: Record<string, any>;
}

/**
 * Template metadata for upload
 */
export interface TemplateMetadata {
  name: string;
  description?: string;
  user_id: string;
  is_ai_generated?: boolean;
}

/**
 * Query options for template retrieval
 */
export interface QueryOptions {
  limit?: number;
  offset?: number;
  sortBy?: keyof Template;
  sortOrder?: 'asc' | 'desc';
  status?: 'pending' | 'approved' | 'rejected';
}

/**
 * Template with file data
 */
export interface TemplateWithFile extends Template {
  file: Blob;
}

/**
 * Task types are now defined in the task-management feature
 */
import type {
  Task as TaskType,
  TaskInsert as TaskInsertType,
  TaskUpdate as TaskUpdateType,
  TaskDocProfile as TaskDocProfileType
} from '@/features/task-management';

export type Task = TaskType;
export type TaskInsert = TaskInsertType;
export type TaskUpdate = TaskUpdateType;
export type TaskDocProfile = TaskDocProfileType;
